import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import { Alert, Button, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FaDesktop, FaMobileScreenButton, FaWifi } from 'react-icons/fa6';
import { GitCompareArrows, RotateCcw, Trophy } from 'lucide-react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TooltipComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { useLocation } from 'react-router-dom';
import { useAppContext } from '@/Main';
import {
  useCompareAnalysisMutation,
  useGetAnalysisHistoryQuery
} from '@/app/PageSpeed/api/analysisApi';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import { IndexerKpiCard } from '@/app/Indexer/components/IndexerKpiCard';
import { analysisColumnConfig } from '@/app/PageSpeed/config/analysisColumnConfig';
import useToolAccess from '@/app/common/hooks/useToolAccess';
import { ToolAccessDenied } from '@/app/common/components/ToolAccessGuard';
import useDomain from '@/hooks/useDomain';
import { getColor } from '@/helpers/utils';
import formatUtcDate from '@/helpers/formatUtcDate';
import { errorMessage } from '@/utils/toast';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import ComparisonPreload from '../components/ComparisonPreload';
import type {
  AnalysisHistoryItem,
  ComparisonPair,
  ComparisonPage,
  ComparisonResponseData
} from '@/app/PageSpeed/api/types/analysisApiTypes';

echarts.use([
  BarChart,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  CanvasRenderer
]);

const TypedAgGridWrapper = AgGridWrapper as ComponentType<{
  columns: unknown[];
  data: unknown[];
  height?: string;
  hideExportHelpAlert?: boolean;
  hideFooter?: boolean;
  settings?: Record<string, unknown>;
  otherOptions?: Record<string, unknown>;
}>;

const metricLabels: Record<string, string> = {
  created_at: 'تاریخ واکاوی',
  settings: 'تنظیمات',
  performance: 'عملکرد',
  seo: 'سئو',
  accessibility: 'دسترس‌پذیری',
  best_practice: 'بهترین روش‌ها',
  site_health: 'سلامت سایت',
  lcp: 'LCP',
  cls: 'CLS',
  tbt: 'TBT',
  fcp: 'FCP',
  si: 'SI',
  tti: 'TTI'
};

const taskIdOf = (row: AnalysisHistoryItem) =>
  String(row.task_id || row._id || '').trim();

const dateValue = (value?: string | number) => {
  const date = value ? new Date(value).getTime() : 0;
  return Number.isNaN(date) ? 0 : date;
};

const Comparison = () => {
  const location = useLocation();
  const { domain, domain_slug } = useDomain();
  const {
    config: { themeVariant }
  } = useAppContext();
  const access = useToolAccess('analysis');
  const { data: historyData, isFetching } = useGetAnalysisHistoryQuery(undefined, {
    skip: access.isLoading || !access.haveAccess,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true
  });
  const [compareAnalysis, { isLoading: isComparing }] =
    useCompareAnalysisMutation();
  const [isManualComparison, setIsManualComparison] = useState(false);
  const navigationState = location.state as
    | { comparisonResult?: ComparisonResponseData }
    | undefined;
  const [compareResult, setCompareResult] = useState<ComparisonResponseData | null>(
    () => navigationState?.comparisonResult || null
  );
  const [showPicker, setShowPicker] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const autoComparedKey = useRef('');

  // Keep completed results, including repeated checks of the same address.
  const comparableRows = useMemo(() => {
    return (historyData?.data || [])
      .filter(row => Number(row.status) === 6 && taskIdOf(row) && row.url)
      .sort((a, b) => dateValue(b.created_at) - dateValue(a.created_at))
      .slice();
  }, [historyData?.data]);

  const latestRows = comparableRows.slice(0, 3);
  const automaticTaskIds = latestRows.map(taskIdOf);
  const availableAddressCount = comparableRows.length;

  const runComparison = async (
    rows: AnalysisHistoryItem[],
    manual = false
  ) => {
    const taskIds = rows.map(taskIdOf).filter(Boolean).slice(0, 3);
    if (taskIds.length < 2) return;
    try {
      const response = await compareAnalysis({
        mode: 'pages',
        task_ids: taskIds,
        strategy: rows[0]?.device_type === 'desktop' ? 'desktop' : 'mobile'
      }).unwrap();
      setCompareResult(response.data || null);
      setIsManualComparison(manual);
      setShowPicker(false);
      setSelectedTaskIds(taskIds);
    } catch (error) {
      console.error(error);
      errorMessage('مقایسه‌ی آدرس‌ها انجام نشد. لطفاً دوباره تلاش کنید.');
    }
  };

  useEffect(() => {
    if (isFetching || compareResult || automaticTaskIds.length < 2) return;
    const key = automaticTaskIds.join(',');
    if (autoComparedKey.current === key) return;
    autoComparedKey.current = key;
    void runComparison(latestRows);
  }, [isFetching, compareResult, automaticTaskIds.join(','), latestRows]);

  const handleSelectionChange = useCallback(
    (taskId: string, selected: boolean, row: AnalysisHistoryItem) => {
      setSelectedTaskIds(ids => {
        if (selected) return ids.length >= 3 ? ids : [...ids, taskId];
        return ids.filter(id => id !== taskId);
      });
      void row;
    },
    []
  );

  const pickerColumns = useMemo(
    () =>
      analysisColumnConfig(domain_slug, domain, {
        hideActions: true,
        selectedTaskIds,
        onSelectionChange: handleSelectionChange
      }),
    [domain_slug, domain, selectedTaskIds, handleSelectionChange]
  );

  const selectedRows = selectedTaskIds
    .map(id => comparableRows.find(row => taskIdOf(row) === id))
    .filter((row): row is AnalysisHistoryItem => Boolean(row));

  if (access.isLoading) return <></>;
  if (!access.haveAccess) {
    return <ToolAccessDenied access={access} tool_label="ابزار واکاوی" />;
  }

  if (isFetching && !historyData) {
    return <ComparisonPreload />;
  }

  return (
    <div dir="rtl" className="pb-4">
      {availableAddressCount < 2 && !isFetching && (
        <Alert variant="info" className="d-flex align-items-center gap-2 fs--1">
          <FontAwesomeIcon icon="info-circle" />
          برای شروع مقایسه، حداقل دو نتیجهٔ تکمیل‌شده لازم است.
          ابتدا نتایج بررسی را تکمیل کنید، سپس برای انتخاب نتایج و مقایسه به این بخش برگردید.
        </Alert>
      )}

      {!compareResult && isComparing && (
        <ComparisonPreload />
      )}

      {!compareResult && showPicker && !isComparing && (
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 className="fw-bold mb-1">انتخاب نتایج برای مقایسه</h5>
                <p className="text-700 fs--1 mb-0">
                  حداقل ۲ و حداکثر ۳ آدرس را انتخاب کنید.
                </p>
              </div>
              <span
                className="badge bg-primary-subtle"
                style={{
                  color: themeVariant === 'primary' ? '#000' : '#fff'
                }}
              >
                {selectedTaskIds.length} از ۳ انتخاب شده
              </span>
            </div>
            <div className="d-flex justify-content-start mb-3" dir="rtl">
              <Button
                variant="falcon-primary"
                style={{
                  color: themeVariant === 'primary' ? '#000' : '#fff'
                }}
                disabled={selectedRows.length < 2 || selectedRows.length > 3}
                onClick={() => void runComparison(selectedRows, true)}
              >
                <GitCompareArrows size={16} className="ms-1" />
                مقایسه
              </Button>
            </div>
            <TypedAgGridWrapper
              columns={pickerColumns}
              data={comparableRows}
              height="560px"
              hideExportHelpAlert
              otherOptions={{ suppressRowVirtualisation: false }}
            />
          </Card.Body>
        </Card>
      )}

      {compareResult?.pages && compareResult.comparison && (
        <ComparisonResult
          result={{
            pages: compareResult.pages,
            comparison: compareResult.comparison,
            strategy: compareResult.strategy
          }}
          showSummary={!isManualComparison}
          onNewComparison={() => {
            setCompareResult(null);
            setIsManualComparison(false);
            setShowPicker(true);
            setSelectedTaskIds([]);
          }}
        />
      )}

      {!compareResult && !showPicker && !isComparing && (
        <div className="text-center">
          <Button
            variant="falcon-primary"
            className="d-inline-flex align-items-center gap-2"
            disabled={availableAddressCount < 2}
            onClick={() => setShowPicker(true)}
          >
            <RotateCcw size={16} />
            مقایسه‌ی جدید
          </Button>
        </div>
      )}
    </div>
  );
};

type ComparisonResultProps = {
  result: {
    pages: ComparisonPage[];
    comparison: ComparisonPair[];
    strategy?: string;
  };
  showSummary: boolean;
  onNewComparison: () => void;
};

const formatDifference = (value?: number) =>
  value === undefined || value === null
    ? '—'
    : value > 0
      ? `${value}+`
      : value < 0
        ? `${Math.abs(value)}-`
        : '0';

const METRIC_KEYS = [
  'performance',
  'seo',
  'accessibility',
  'best_practice',
  'site_health',
  'lcp',
  'cls',
  'tbt',
  'fcp',
  'si',
  'tti'
] as const;

type ComparisonRow = {
  metric: string;
  differenceByPage?: Record<number, number[]>;
  winnerByPage?: Record<number, boolean[]>;
  [key: string]: unknown;
};

const pageLetter = (index: number) => String.fromCharCode(65 + index);

const findPageIndex = (
  pages: ComparisonPage[],
  ref?: { task_id?: string; url?: string }
) => {
  if (!ref?.task_id) return -1;
  return pages.findIndex(page => page.task_id === ref.task_id);
};

const ComparisonSettingsCell = ({ value }: { value?: unknown }) => {
  const settings = (value || {}) as {
    location_code?: string;
    internet_type?: string;
    server?: string;
  };
  const server = settings.server || '';

  return (
    <div className="d-flex align-items-center gap-3 text-800 fs--1">
      <ChildrensTooltip
        placement="top"
        delayToRemove={0}
        html={
          settings.location_code === 'not_ir'
            ? 'خارج'
            : settings.location_code === 'google'
              ? 'گوگل'
              : settings.location_code || 'مکان نامشخص'
        }
      >
        {settings.location_code === 'not_ir' ? (
          <FontAwesomeIcon icon="globe" />
        ) : settings.location_code === 'google' ? (
          <FontAwesomeIcon icon={['fab', 'google']} />
        ) : settings.location_code ? (
          <img
            src={`https://static.mizfa.tools/flags/${settings.location_code}.webp`}
            alt={settings.location_code}
            style={{ width: 18 }}
          />
        ) : (
          <FontAwesomeIcon icon="globe" />
        )}
      </ChildrensTooltip>
      <ChildrensTooltip
        placement="top"
        delayToRemove={0}
        html={settings.internet_type || 'نوع اینترنت نامشخص'}
      >
        <FaWifi />
      </ChildrensTooltip>
      <ChildrensTooltip
        placement="top"
        delayToRemove={0}
        html={server === 'desktop' ? 'رایانه' : 'گوشی'}
      >
        {server === 'desktop' ? <FaDesktop /> : <FaMobileScreenButton />}
      </ChildrensTooltip>
    </div>
  );
};

const ComparisonMetricCell = ({
  value,
  data,
  pageIndex
}: {
  value?: unknown;
  data: ComparisonRow;
  pageIndex: number;
}) => {
  const differences = data.differenceByPage?.[pageIndex] || [];
  const winners = data.winnerByPage?.[pageIndex] || [];
  const isPerfectScore =
    [
      'performance',
      'seo',
      'accessibility',
      'best_practice',
      'site_health'
    ].includes(data.metric) && Number(value) === 100;

  return (
    <div className="d-flex align-items-center justify-content-between gap-2 text-800 fs--1">
      <div className="d-flex align-items-center gap-2">
        <span className="fw-bold">
          {value === undefined || value === null || value === ''
            ? '—'
            : String(value)}
        </span>
        {differences.map((difference, index) => (
          difference !== 0 && (
            <span
              key={`${difference}-${index}`}
              className={`badge rounded-pill ${
                winners[index] || isPerfectScore
                  ? 'bg-success-subtle text-success'
                  : 'bg-danger-subtle text-danger'
              }`}
            >
              {formatDifference(difference)}
            </span>
          )
        ))}
      </div>
    </div>
  );
};

const ComparisonTable = ({
  pages,
  comparison
}: {
  pages: ComparisonPage[];
  comparison: ComparisonPair[];
}) => {
  const columns = useMemo(() => {
    return [
      {
        field: 'metric',
        title: 'شاخص',
        width: 180,
        cellRenderer: (params: { value: string }) => (
          <span className="text-800 fs--1 fw-bold">
            {metricLabels[params.value] || params.value}
          </span>
        )
      },
      ...pages.map((page, pageIndex) => ({
        field: `page_${pageIndex}`,
        title: `آدرس ${pageLetter(pageIndex)}`,
        flex: 1,
        minWidth: 180,
        cellRenderer: (params: { value?: unknown; data: ComparisonRow }) => {
          if (params.data.metric === 'settings') {
            return <ComparisonSettingsCell value={params.value} />;
          }
          return (
            <ComparisonMetricCell
              value={params.value}
              data={params.data}
              pageIndex={pageIndex}
            />
          );
        }
      }))
    ];
  }, [pages]);

  const rows = useMemo(() => {
    const metadataRows: ComparisonRow[] = ['created_at', 'settings'].map(
      metric => {
        const row: ComparisonRow = { metric };
        pages.forEach((page, index) => {
          row[`page_${index}`] =
            metric === 'created_at'
              ? formatUtcDate(page.created_at as string | undefined)
              : {
                  location_code: page.location_code,
                  internet_type: page.internet_type,
                  server: page.server || page.device_type
                };
        });
        return row;
      }
    );

    const comparisonRows = METRIC_KEYS.map(metric => {
      const row: ComparisonRow = {
        metric,
        differenceByPage: {},
        winnerByPage: {}
      };
      pages.forEach((page, index) => {
        row[`page_${index}`] = page[metric];
      });
      return row;
    });

    // The API returns adjacent pairs: A→B and then B→C.
    // Keep one metric row and collect both comparisons in the relevant cells.
    comparison.forEach(pair => {
      const fromIndex = findPageIndex(pages, pair.from);
      const toIndex = findPageIndex(pages, pair.to);
      if (fromIndex < 0 || toIndex < 0) return;

      comparisonRows.forEach(row => {
        const details = pair[row.metric as typeof METRIC_KEYS[number]];
        if (!details) return;

        row.differenceByPage![fromIndex] = [
          ...(row.differenceByPage![fromIndex] || []),
          -(details.difference ?? 0)
        ];
        row.differenceByPage![toIndex] = [
          ...(row.differenceByPage![toIndex] || []),
          details.difference ?? 0
        ];
        row.winnerByPage![fromIndex] = [
          ...(row.winnerByPage![fromIndex] || []),
          details.winner === 'page-a'
        ];
        row.winnerByPage![toIndex] = [
          ...(row.winnerByPage![toIndex] || []),
          details.winner === 'page-b'
        ];
      });
    });

    return [...metadataRows, ...comparisonRows];
  }, [pages, comparison]);

  return (
    <div className="mb-4">
      <div className="mb-3">
        <p className="text-700 fs--2 mb-0">
          مقایسه‌های متوالی طبق پاسخ API: آدرس A با B و سپس آدرس B با C
        </p>
      </div>
      <TypedAgGridWrapper
        columns={columns}
        data={rows}
        height="auto"
        hideExportHelpAlert
        hideFooter
        settings={{ pagination: false }}
      />
    </div>
  );
};

const ComparisonResult = ({
  result,
  showSummary,
  onNewComparison
}: ComparisonResultProps) => {
  const scoreMetricKeys = [
    'performance',
    'seo',
    'accessibility',
    'best_practice',
    'site_health'
  ] as const;
  const scoreMetricNames = ['عملکرد', 'سئو', 'دسترسی‌پذیری', 'بهترین روش‌ها', 'سلامت سایت'];
  const speedMetricKeys = ['lcp', 'fcp', 'tbt', 'si', 'tti'] as const;
  const speedMetricNames = ['LCP', 'FCP', 'TBT', 'SI', 'TTI'];
  const chartColors = ['#2c7be5', '#00d97e', '#f6c343'];
  const chartTextColor = getColor('gray-800');
  const chartGridColor = getColor('gray-300');
  const chartFontFamily = 'dana, sans-serif';
  const tooltipBackgroundColor = getColor('gray-100');
  const tooltipBorderColor = getColor('gray-300');
  const tooltipTextColor = getColor('gray-1100');

  const scoreChartOption = useMemo(
    () => ({
      textStyle: { fontFamily: chartFontFamily },
      tooltip: {
        trigger: 'axis',
        backgroundColor: tooltipBackgroundColor,
        borderColor: tooltipBorderColor,
        borderWidth: 1,
        textStyle: {
          color: tooltipTextColor,
          fontFamily: chartFontFamily,
          fontSize: 12
        }
      },
      legend: {
        bottom: 8,
        textStyle: { color: chartTextColor, fontFamily: chartFontFamily }
      },
      grid: { left: 42, right: 18, top: 18, bottom: 68 },
      xAxis: {
        type: 'category',
        data: scoreMetricNames,
        axisLabel: {
          color: chartTextColor,
          interval: 0,
          fontFamily: chartFontFamily,
          fontSize: 12,
          fontWeight: 600
        }
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        axisLabel: { color: chartTextColor, fontFamily: chartFontFamily },
        splitLine: { lineStyle: { color: chartGridColor } }
      },
      series: result.pages.map((page, index) => ({
        name: `آدرس ${String.fromCharCode(65 + index)}`,
        type: 'bar',
        barMaxWidth: 22,
        itemStyle: { color: chartColors[index] },
        data: scoreMetricKeys.map(key => page[key] ?? 0)
      }))
    }),
    [
      result,
      chartFontFamily,
      chartGridColor,
      chartTextColor,
      tooltipBackgroundColor,
      tooltipBorderColor,
      tooltipTextColor
    ]
  );

  const speedChartOption = useMemo(
    () => ({
      textStyle: { fontFamily: chartFontFamily },
      tooltip: {
        trigger: 'axis',
        backgroundColor: tooltipBackgroundColor,
        borderColor: tooltipBorderColor,
        borderWidth: 1,
        textStyle: {
          color: tooltipTextColor,
          fontFamily: chartFontFamily
        },
        valueFormatter: (value: number) => `${value} ms`
      },
      legend: {
        bottom: 8,
        textStyle: {
          color: chartTextColor,
          fontFamily: chartFontFamily,
          fontSize: 12
        }
      },
      grid: { left: 42, right: 18, top: 18, bottom: 68 },
      xAxis: {
        type: 'category',
        data: speedMetricNames,
        axisLabel: {
          color: chartTextColor,
          fontFamily: chartFontFamily,
          fontSize: 12,
          fontWeight: 600
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: chartTextColor, fontFamily: chartFontFamily },
        splitLine: { lineStyle: { color: chartGridColor } }
      },
      series: result.pages.map((page, index) => ({
        name: `آدرس ${String.fromCharCode(65 + index)}`,
        type: 'bar',
        barMaxWidth: 22,
        itemStyle: { color: chartColors[index] },
        data: speedMetricKeys.map(key => page[key] ?? 0)
      }))
    }),
    [
      result,
      chartFontFamily,
      chartGridColor,
      chartTextColor,
      tooltipBackgroundColor,
      tooltipBorderColor,
      tooltipTextColor
    ]
  );

  return (
    <div className="pb-4">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <Trophy size={21} className="text-warning" />
              <h5 className="mb-0 fw-bold">نتیجه مقایسه</h5>
            </div>
            {showSummary && (
              <p className="mb-0 text-700 fs--1">
                سه آدرس آخر باهم مقایسه شدند.
              </p>
            )}
          </div>
          <Button
            variant="outline-primary"
            size="sm"
            className="d-inline-flex align-items-center gap-2"
            onClick={onNewComparison}
          >
            <RotateCcw size={14} />
            مقایسه‌ی جدید
          </Button>
        </div>

        <div className="row g-3 mb-4">
          {result.pages.map((page, index) => (
            <div className="col-12 col-lg-4" key={page.task_id || page.url || index}>
              <IndexerKpiCard
                tone={index === 0 ? 'primary' : index === 1 ? 'success' : 'warning'}
                imageIndex={index}
                className="h-100"
              >
                  <div className="d-flex align-items-start gap-2 mb-3">
                    <span
                      className={`badge ${
                        index === 0 ? 'bg-primary' : 'bg-secondary'
                      } rounded-circle`}
                    >
                      {String.fromCharCode(65 + index)}
                    </span>
                    <span
                      className="text-truncate flex-grow-1 fw-bold fs--1"
                      style={{ minWidth: 0 }}
                      dir="ltr"
                      title={page.url}
                    >
                      {page.url || 'آدرس نامشخص'}
                    </span>
                  </div>
                  <div className="d-flex align-items-end justify-content-between">
                    <div>
                      <div className="text-700 fs--2 mb-1">امتیاز سلامت</div>
                      <div className="fs-1 fw-bold text-800">
                        {page.site_health ?? '—'}
                      </div>
                    </div>
                    <div className="text-end fs--2 text-700">
                      <div>سئو: {page.seo ?? '—'}</div>
                      <div>عملکرد: {page.performance ?? '—'}</div>
                      <div>دسترسی‌پذیری: {page.accessibility ?? '—'}</div>
                    </div>
                  </div>
              </IndexerKpiCard>
            </div>
          ))}
        </div>

        <div className="row g-3 mb-4">
          <div className="col-12 col-xl-6">
            <Card className="h-100 border shadow-sm">
              <Card.Header className="bg-body-tertiary">
                <h6 className="fw-bold mb-1">مقایسه امتیازها</h6>
                <p className="text-700 fs--2 mb-0">
                  امتیاز معیارهای اصلی هر آدرس را کنار هم ببینید.
                </p>
              </Card.Header>
              <Card.Body>
                <ReactEChartsCore
                  echarts={echarts}
                  option={scoreChartOption}
                  style={{ height: 290 }}
                  notMerge
                />
              </Card.Body>
            </Card>
          </div>
          <div className="col-12 col-xl-6">
            <Card className="h-100 border shadow-sm">
              <Card.Header className="bg-body-tertiary">
                <h6 className="fw-bold mb-1">مقایسه سرعت</h6>
                <p className="text-700 fs--2 mb-0">
                  مقدار معیارهای سرعت برحسب میلی‌ثانیه نمایش داده شده است.
                </p>
              </Card.Header>
              <Card.Body>
                <ReactEChartsCore
                  echarts={echarts}
                  option={speedChartOption}
                  style={{ height: 290 }}
                  notMerge
                />
              </Card.Body>
            </Card>
          </div>
        </div>

        <h6 className="fw-bold mb-3">جزئیات مقایسه</h6>
        <ComparisonTable
          pages={result.pages}
          comparison={result.comparison}
        />
    </div>
  );
};

export default Comparison;
